/**
 * The count thresholds of ONE layer: an and/or tree of like/reply/repost
 * gates, edited in place.
 *
 * Lifted out of the Feed shape unchanged when layers became blocks. Thresholds
 * are an ordering's business — "only posts with at least 5 likes" belongs to
 * the Trending layer, not to the feed as a whole — and the serve path already
 * reads them per lane (`feed_lenses` merges a lane's keys over the feed's).
 * A single-layer feed still writes them to the feed-level key it always did.
 */

import {
  ChevronDownIcon,
  ChevronUpIcon,
  Cross2Icon,
  PlusIcon,
} from "@radix-ui/react-icons";
import delve from "dlv";
import { dset } from "dset";
import { klona } from "klona";

import { BlockContainer } from "@/components/canvas-editor/components/Container";
import {
  CanvasButton,
  CanvasNumberInput,
  CanvasSimpleSelect,
  OperatorSelect,
} from "@/components/canvas-editor/components/Inputs";
import {
  logicalOps,
  numberOperators,
} from "@/components/canvas-editor/shapes/shapes";
import { preventBubble } from "@/components/canvas-editor/utils";
import {
  DropdownMenu,
  MenuContent,
  MenuItem,
  MenuSeparator,
  MenuTrigger,
} from "@/components/ui/menu";
import {
  ThresholdFilters,
  ThresholdLeafValue,
  Thresholds,
} from "@/lib/types";
import { cn } from "@/lib/utils";

/** The layer's thresholds editor: the root and/or select plus its rows. */
export function ThresholdsBlock({
  thresholds,
  onChange,
}: {
  thresholds: Thresholds | null | undefined;
  onChange: (thresholds?: Thresholds) => void;
}) {
  const rootLogicalOp = thresholds && "OR" in thresholds ? "OR" : "AND";
  return (
    <div className="sort-area">
      <FeedSortingLogicalBlock
        path={[rootLogicalOp]}
        thresholds={thresholds || { AND: [] }}
        updateThresholds={onChange}
      />
    </div>
  );
}

function FeedSortingLogicalBlock({
  updateThresholds,
  path,
  thresholds,
}: {
  path: string[];
  thresholds: Thresholds;
  updateThresholds: (newThresholds?: Thresholds) => void;
}) {
  const key = path[path.length - 1];

  if (!key) {
    return null;
  }

  const values = delve(thresholds, path) as ThresholdFilters[];
  const onAddThreshold = (
    type:
      | "reply_count"
      | "like_count"
      | "repost_count"
      | "reader_reply_count"
      | "reader_like_count"
      | "reader_repost_count"
      | "request_less_count"
      | "request_more_count",
  ) => {
    const newThresholds = klona(thresholds);
    const index = (delve(newThresholds, path) as ThresholdFilters[]).length;
    dset(newThresholds, [...path, index], { [type]: { op: ">=", value: 0 } });
    updateThresholds(newThresholds);
  };
  const onAddThresholdLogic = (type: "AND" | "OR") => {
    const newThresholds = klona(thresholds);
    const index = (delve(newThresholds, path) as ThresholdFilters[]).length;
    dset(newThresholds, [...path, index], { [type]: [] });
    updateThresholds(newThresholds);
  };
  const onMove = (index: number, offset: 1 | -1, root?: boolean) => {
    const newThresholds = klona(thresholds);

    if (index === undefined) return;

    const p = root ? path.slice(0, -2) : path;

    const currentValue = delve(newThresholds, [
      ...p,
      `${index}`,
    ]) as ThresholdLeafValue;
    const switchValue = delve(newThresholds, [
      ...p,
      index + offset,
    ]) as ThresholdLeafValue;

    dset(newThresholds, [...p, index], switchValue);
    dset(newThresholds, [...p, index + offset], currentValue);
    updateThresholds(newThresholds);
  };

  const previousKey = path[path.length - 2];
  const logicIndex = previousKey ? parseInt(previousKey) : undefined;
  const parentValues = previousKey
    ? (delve(thresholds, path.slice(0, -2)) as [])
    : [];

  return (
    // The root row sits inside a layer's body, which brings its own padding;
    // nested groups keep theirs inside their own frame.
    <div className={cn("flex flex-col gap-2", path.length === 1 ? "p-0" : "p-4")}>
      <div className="flex gap-2 pointer-events-auto relative sort-row">
        <CanvasSimpleSelect
          aria-label="Logical Operator"
          value={key?.toLowerCase()}
          placeholder="Type"
          className="flex-1"
          options={logicalOps}
          onValueChange={(v) => {
            const newThresholds = klona(thresholds);
            const realKey = v.toUpperCase();

            if (path.length === 1) {
              delete newThresholds[key as keyof Thresholds];
              dset(newThresholds, realKey, delve(thresholds, path));
            } else {
              const parentPath = path.slice(0, -1);
              dset(newThresholds, parentPath, {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                [realKey]: delve(thresholds, path),
              });
            }

            updateThresholds(newThresholds);
          }}
        />
        <div
          className={cn(
            "hidden sort-area-control absolute top-1/2 -translate-y-1/2 right-0 translate-x-full px-1",
          )}
        >
          <div className="flex gap-1 bg-slate-subtle p-1 rounded-lg border border-slate-normal">
            {logicIndex !== undefined && (
              <>
                <CanvasButton
                  variant="ghost"
                  size="smIcon"
                  onPress={() => onMove(logicIndex, 1, true)}
                  disabled={logicIndex === parentValues.length - 1}
                >
                  <ChevronDownIcon />
                </CanvasButton>
                <CanvasButton
                  variant="ghost"
                  size="smIcon"
                  onPress={() => onMove(logicIndex, -1, true)}
                  disabled={logicIndex === 0}
                >
                  <ChevronUpIcon />
                </CanvasButton>
                <CanvasButton
                  variant="ghost"
                  size="smIcon"
                  onPress={() => {
                    const newThresholds = klona(thresholds);
                    const parentPath = path.slice(0, -2);
                    const newValues = delve(
                      newThresholds,
                      parentPath,
                    ) as ThresholdFilters[];
                    newValues.splice(logicIndex, 1);
                    dset(newThresholds, parentPath, newValues);
                    updateThresholds(newThresholds);
                  }}
                >
                  <Cross2Icon />
                </CanvasButton>
              </>
            )}
            <DropdownMenu>
              <MenuTrigger
                asChild
                className="hidden sort-area-control data-[state=open]:flex"
              >
                <CanvasButton variant="ghost" size="smIcon" isTrigger={true}>
                  <PlusIcon />
                </CanvasButton>
              </MenuTrigger>
              <MenuContent {...preventBubble} sideOffset={12}>
                <MenuItem
                  onSelect={() => {
                    onAddThreshold("reply_count");
                  }}
                >
                  Reply Count
                </MenuItem>
                <MenuItem onSelect={() => onAddThreshold("like_count")}>
                  Like Count
                </MenuItem>
                <MenuItem onSelect={() => onAddThreshold("repost_count")}>
                  Repost Count
                </MenuItem>
                <MenuItem onSelect={() => onAddThreshold("reader_like_count")}>
                  Reader Like Count
                </MenuItem>
                <MenuItem onSelect={() => onAddThreshold("reader_reply_count")}>
                  Reader Reply Count
                </MenuItem>
                <MenuItem
                  onSelect={() => onAddThreshold("reader_repost_count")}
                >
                  Reader Repost Count
                </MenuItem>
                <MenuItem onSelect={() => onAddThreshold("request_less_count")}>
                  Request Less Count
                </MenuItem>
                <MenuItem onSelect={() => onAddThreshold("request_more_count")}>
                  Request More Count
                </MenuItem>
                <MenuSeparator />
                <MenuItem onSelect={() => onAddThresholdLogic("AND")}>
                  And
                </MenuItem>
                <MenuItem onSelect={() => onAddThresholdLogic("OR")}>
                  Or
                </MenuItem>
              </MenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
      {values &&
        values.length > 0 &&
        values.map((v, index) => {
          const key = Object.keys(v)[0];

          if (!key) {
            return null;
          }

          if (key === "AND" || key === "OR") {
            return (
              <BlockContainer
                variant="amber"
                asDiv={true}
                // eslint-disable-next-line @eslint-react/no-array-index-key
                key={`${key}-${index}`}
                className="my-2"
              >
                <FeedSortingLogicalBlock
                  // eslint-disable-next-line @eslint-react/no-array-index-key
                  key={`${key}-${index}`}
                  path={[...path, `${index}`, key]}
                  thresholds={thresholds}
                  updateThresholds={updateThresholds}
                />
              </BlockContainer>
            );
          }

          const data = delve(thresholds, [
            ...path,
            index,
            key,
          ]) as ThresholdLeafValue;

          return (
            <div
              // eslint-disable-next-line @eslint-react/no-array-index-key
              key={`${key}-${index}`}
              className="pointer-events-auto flex flex-col gap-2 relative sort-row"
            >
              <div className="flex gap-2">
                <CanvasSimpleSelect
                  aria-label="Field"
                  value={key}
                  className="w-1/3"
                  options={[
                    "like_count",
                    "reply_count",
                    "repost_count",
                    "reader_like_count",
                    "reader_reply_count",
                    "reader_repost_count",
                    "request_less_count",
                    "request_more_count",
                  ]}
                  onValueChange={(v) => {
                    const newThresholds = klona(thresholds);
                    const old = delve(newThresholds, [
                      ...path,
                      index,
                    ]) as Record<string, unknown>;

                    delete old[key];
                    dset(newThresholds, [...path, index, v], data);
                    updateThresholds(newThresholds);
                  }}
                />
                <OperatorSelect
                  value={data.op}
                  validOperators={numberOperators}
                  onValueChange={(v) => {
                    const newThresholds = klona(thresholds);
                    dset(newThresholds, [...path, index, key, "op"], v);
                    updateThresholds(newThresholds);
                  }}
                />
                <CanvasNumberInput
                  placeholder="Threshold"
                  className="flex-1"
                  value={data.value}
                  min={0}
                  onValueChange={(v) => {
                    const newThresholds = klona(thresholds);
                    dset(newThresholds, [...path, index, key, "value"], v);
                    updateThresholds(newThresholds);
                  }}
                />

                <div className="absolute top-1/2 -translate-y-1/2 right-0 translate-x-full px-1">
                  <div className="hidden sort-area-control gap-1 bg-slate-subtle p-1 rounded-lg border border-slate-normal">
                    <CanvasButton
                      variant="ghost"
                      size="smIcon"
                      onPress={() => onMove(index, 1)}
                      disabled={index === values.length - 1}
                    >
                      <ChevronDownIcon />
                    </CanvasButton>
                    <CanvasButton
                      variant="ghost"
                      size="smIcon"
                      onPress={() => onMove(index, -1)}
                      disabled={index === 0}
                    >
                      <ChevronUpIcon />
                    </CanvasButton>
                    <CanvasButton
                      variant="ghost"
                      size="smIcon"
                      onPress={() => {
                        const newThresholds = klona(thresholds);
                        const newValues = delve(
                          newThresholds,
                          path,
                        ) as ThresholdFilters[];
                        newValues.splice(index, 1);
                        dset(newThresholds, path, newValues);
                        updateThresholds(newThresholds);
                      }}
                    >
                      <Cross2Icon />
                    </CanvasButton>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
    </div>
  );
}

